/* Le catalogue d'une usine, ouvert par-dessus le devis.
 *
 * Pourquoi un panneau et pas une table sous les lignes : à quatre-vingts
 * références — trois cent soixante chez Aromasol — choisir devient un travail
 * à part. On veut voir le catalogue en entier, cocher ce qu'on prend, et
 * revenir au devis avec tout d'un coup.
 *
 * Pourquoi des ARTICLES et pas des références : « Cumin En Grains » existe en
 * pot, en sachet, en sac et en tube, et chaque format est une référence. Les
 * lister à la suite, c'est faire défiler quatre fois le même produit. On
 * affiche donc un article par ligne et ses conditionnements côte à côte : la
 * liste est trois fois plus courte, et choisir le format devient le geste
 * d'ajout lui-même.
 *
 * Trois façons d'ajouter, selon la main : le clic coche un format ; la touche
 * Entrée prend le seul format qui reste à l'écran et vide la recherche, pour
 * enchaîner au clavier ; « Tout prendre » coche ce que le filtre montre.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Drawer } from "@/ui/Drawer";
import { Icon } from "@/ui/Icon";
import { Empty } from "@/ui/primitives";
import { euro } from "@/lib/format";
import { SANS_GAMME, filtrer, gammes, grouperArticles, libelleFormat } from "@/domain/catalogue";
import { parCarton } from "@/domain/devis";
import type { Produit } from "@/types/metier";

export function Choisir({
  usineId, nomUsine, produits, dejaLa, prixDe, onAjouter, onClose,
}: {
  usineId: string;
  nomUsine: string;
  produits: readonly Produit[];
  /** Ce qui est déjà au devis : on ne le repropose pas. */
  dejaLa: ReadonlySet<string>;
  /** Le prix auquel la ligne partira, pour qu'on choisisse en connaissance. */
  prixDe: (produitId: string) => number;
  onAjouter: (ids: string[]) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const [gamme, setGamme] = useState<string | null>(null);
  const [pris, setPris] = useState<string[]>([]);
  const champ = useRef<HTMLInputElement>(null);

  useEffect(() => { champ.current?.focus(); }, []);

  const lesGammes = useMemo(() => gammes(produits, usineId), [produits, usineId]);
  const visibles = useMemo(
    () => filtrer(produits, { usineId, q, gamme, exclure: dejaLa }),
    [produits, usineId, q, gamme, dejaLa],
  );
  const articles = useMemo(() => grouperArticles(visibles), [visibles]);

  /* Le total de l'usine, hors recherche : c'est le repère qui dit si le filtre
     cache beaucoup ou presque rien. */
  const tout = useMemo(
    () => filtrer(produits, { usineId, exclure: dejaLa }).length,
    [produits, usineId, dejaLa],
  );

  const choisi = new Set(pris);
  const basculer = (id: string) =>
    setPris((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));

  /* Entrée ne devine pas : elle n'agit que s'il ne reste qu'un format. */
  function auClavier(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const seul = visibles.length === 1 ? visibles[0] : undefined;
    if (!seul) return;
    if (!choisi.has(seul.id)) basculer(seul.id);
    setQ("");
  }

  const toutPrendre = () => {
    const ids = visibles.map((p) => p.id);
    setPris((l) => [...l, ...ids.filter((id) => !l.includes(id))]);
  };

  const valider = () => {
    if (!pris.length) return;
    onAjouter(pris);
    onClose();
  };

  const filtre = Boolean(q.trim() || gamme);

  return (
    <Drawer
      large
      titre="Catalogue"
      sous={
        `${filtre ? `${visibles.length} sur ${tout}` : tout} référence${tout > 1 ? "s" : ""}` +
        ` · ${articles.length} article${articles.length > 1 ? "s" : ""} — ${nomUsine}`
      }
      onClose={onClose}
      pied={
        <>
          {visibles.length > 1 ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={toutPrendre}>
              Tout prendre ({visibles.length})
            </button>
          ) : null}
          {pris.length ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPris([])}>
              Vider la sélection
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!pris.length}
            onClick={valider}
          >
            {pris.length
              ? `Ajouter ${pris.length} référence${pris.length > 1 ? "s" : ""}`
              : "Ajouter au devis"}
          </button>
        </>
      }
    >
      <div className="cat-barre">
        <label className="tsearch cat-cherche">
          <Icon n="recherche" cls="" />
          <input
            ref={champ}
            type="search"
            value={q}
            placeholder="Référence, désignation, conditionnement…"
            aria-label="Chercher une référence"
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={auClavier}
          />
        </label>
        {lesGammes.length > 1 ? (
          <div className="cat-gammes">
            <button
              type="button"
              className="pill"
              aria-pressed={gamme === null}
              onClick={() => setGamme(null)}
            >
              Toutes
            </button>
            {lesGammes.map((g) => (
              <button
                key={g.nom}
                type="button"
                className="pill"
                aria-pressed={gamme === g.nom}
                onClick={() => setGamme(gamme === g.nom ? null : g.nom)}
              >
                {g.nom === SANS_GAMME ? "Sans gamme" : g.nom}
                <span className="n">{g.n}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {articles.length === 0 ? (
        <Empty
          titre={tout === 0 ? "Tout est déjà au devis" : "Aucune référence"}
          detail={
            tout === 0
              ? "Les références de cette usine figurent toutes dans les lignes."
              : "Essayez moins de mots, ou retirez le filtre de gamme."
          }
        />
      ) : (
        <ul className="cat-l">
          {articles.map((a) => (
            <li key={a.nom} className="cat-art">
              <div className="cat-art-nom">{a.nom}</div>
              <div className="cat-formats">
                {a.formats.map((f, _k, tous) => {
                  const actif = choisi.has(f.id);
                  const par = parCarton(f.par_carton);
                  const { nom: cond, poids } = libelleFormat(f);
                  /* Deux formats au même libellé seraient deux pastilles
                     jumelles : on les départage par la référence, sinon on
                     met au devis l'une pour l'autre sans jamais le voir. */
                  const jumeau =
                    tous.filter((x) => libelleFormat(x).nom === cond).length > 1;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className="cat-f"
                      aria-pressed={actif}
                      title={`${f.ref} — ${par > 1 ? `${par} par carton` : "à l'unité"}`}
                      onClick={() => basculer(f.id)}
                    >
                      <span className="cat-f-case" aria-hidden="true">
                        {actif ? <Icon n="coche" cls="" /> : null}
                      </span>
                      <span className="cat-f-cond">
                        {cond}
                        {poids ? <span className="cat-f-poids">· {poids}</span> : null}
                        {jumeau ? <span className="cat-f-ref">{f.ref}</span> : null}
                      </span>
                      <span className="cat-f-prix mono">{euro(prixDe(f.id))}</span>
                      {par > 1 ? <span className="cat-f-par">×{par}</span> : null}
                    </button>
                  );
                })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Drawer>
  );
}
